import React, { useMemo } from 'react';

interface LineChartProps {
  data: Array<{
    date: string;
    purchases: { count: number; revenue: number; unique_buyers: number };
    payments: { count: number; revenue: number; avg_delay: number | null };
    bookings: { count: number; unique_users: number; unique_events: number };
    cumulative: {
      purchases: number;
      revenue: number;
      payments: number;
      bookings: number;
    };
  }>;
  type: 'revenue' | 'activity' | 'cumulative';
  labels?: {
    purchases?: string;
    bookings?: string;
  };
}

const LineChart: React.FC<LineChartProps> = ({ data, type, labels }) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { max: 0, points: [], clusterSize: 1 };

    // Aggregate data by month for better visualization
    const monthlyData = new Map<string, any>();

    data.forEach(day => {
      const month = day.date.substring(0, 7); // YYYY-MM
      if (!monthlyData.has(month)) {
        monthlyData.set(month, {
          purchases: 0,
          payments: 0,
          bookings: 0,
          revenue: 0,
          paidRevenue: 0
        });
      }

      const monthData = monthlyData.get(month);
      monthData.purchases += day.purchases.count;
      monthData.payments += day.payments.count;
      monthData.bookings += day.bookings.count;
      monthData.revenue += day.purchases.revenue;
      monthData.paidRevenue += day.payments.revenue;
    });

    let points = Array.from(monthlyData.entries()).map(([month, data]) => ({
      month,
      ...data
    }));

    // Intelligent clustering based on number of months
    const totalMonths = points.length;
    let clusterSize = 1;

    if (totalMonths > 120) {
      // > 10 years: aggregate by year (12 months)
      clusterSize = 12;
    } else if (totalMonths > 60) {
      // > 5 years: aggregate by half-year (6 months)
      clusterSize = 6;
    } else if (totalMonths > 36) {
      // > 3 years: aggregate by quarter (3 months)
      clusterSize = 3;
    } else if (totalMonths > 24) {
      // > 2 years: aggregate by 2 months
      clusterSize = 2;
    }

    // Cluster data if needed
    if (clusterSize > 1) {
      const clusteredPoints: any[] = [];
      for (let i = 0; i < points.length; i += clusterSize) {
        const cluster = points.slice(i, i + clusterSize);
        const firstMonth = cluster[0].month;
        const lastMonth = cluster[cluster.length - 1].month;

        clusteredPoints.push({
          month: clusterSize === 12 ? firstMonth.substring(0, 4) : // Year only
                 clusterSize === 6 ? `${firstMonth} - ${lastMonth}` : // Range
                 firstMonth, // Quarter/bi-monthly
          purchases: cluster.reduce((sum, p) => sum + p.purchases, 0),
          payments: cluster.reduce((sum, p) => sum + p.payments, 0),
          bookings: cluster.reduce((sum, p) => sum + p.bookings, 0),
          revenue: cluster.reduce((sum, p) => sum + p.revenue, 0),
          paidRevenue: cluster.reduce((sum, p) => sum + p.paidRevenue, 0),
          isCluster: true,
          clusterMonths: cluster.map(c => c.month)
        });
      }
      points = clusteredPoints;
    }

    let max = 0;
    if (type === 'revenue') {
      max = Math.max(...points.map(p => Math.max(p.revenue, p.paidRevenue)));
    } else if (type === 'activity') {
      max = Math.max(...points.map(p => Math.max(p.purchases, p.bookings)));
    }

    return { max, points, clusterSize };
  }, [data, type]);

  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400">
        Keine Daten verfügbar
      </div>
    );
  }

  const { max, points, clusterSize } = chartData;
  const height = 256; // h-64 in pixels
  const padding = 40;
  const chartHeight = height - padding * 2;

  const getY = (value: number) => {
    if (max === 0) return chartHeight + padding;
    return padding + chartHeight - (value / max) * chartHeight;
  };

  // Fixed width - clustering ensures we never have too many points
  const chartWidth = 800;
  const xStep = (chartWidth - padding * 2) / Math.max(points.length - 1, 1);

  return (
    <div className="relative">
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${chartWidth} ${height}`}
      >
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(fraction => {
          const y = padding + chartHeight * (1 - fraction);
          return (
            <g key={fraction}>
              <line
                x1={padding}
                y1={y}
                x2={chartWidth - padding}
                y2={y}
                stroke="currentColor"
                strokeOpacity="0.1"
                className="text-gray-400"
              />
              <text
                x={padding - 5}
                y={y + 4}
                textAnchor="end"
                className="text-xs fill-gray-500"
              >
                {type === 'revenue' 
                  ? `€${Math.round((max * fraction) / 100) * 100}`
                  : Math.round(max * fraction)
                }
              </text>
            </g>
          );
        })}

        {/* Data lines */}
        {type === 'revenue' ? (
          <>
            {/* Revenue line */}
            <polyline
              points={points.map((p, i) => `${padding + i * xStep},${getY(p.revenue)}`).join(' ')}
              fill="none"
              stroke="rgb(34 197 94)"
              strokeWidth="2"
              className="drop-shadow-sm"
            />
            {/* Paid revenue line */}
            <polyline
              points={points.map((p, i) => `${padding + i * xStep},${getY(p.paidRevenue)}`).join(' ')}
              fill="none"
              stroke="rgb(59 130 246)"
              strokeWidth="2"
              strokeDasharray="5,5"
              className="drop-shadow-sm"
            />
            {/* Data points */}
            {points.map((p, i) => (
              <g key={i}>
                <circle
                  cx={padding + i * xStep}
                  cy={getY(p.revenue)}
                  r="4"
                  fill="rgb(34 197 94)"
                  className="hover:r-6 transition-all cursor-pointer"
                >
                  <title>{`${p.month}: €${p.revenue.toFixed(2)} Gesamt`}</title>
                </circle>
                <circle
                  cx={padding + i * xStep}
                  cy={getY(p.paidRevenue)}
                  r="4"
                  fill="rgb(59 130 246)"
                  className="hover:r-6 transition-all cursor-pointer"
                >
                  <title>{`${p.month}: €${p.paidRevenue.toFixed(2)} Bezahlt`}</title>
                </circle>
              </g>
            ))}
          </>
        ) : (
          <>
            {/* Purchases line */}
            <polyline
              points={points.map((p, i) => `${padding + i * xStep},${getY(p.purchases)}`).join(' ')}
              fill="none"
              stroke="rgb(59 130 246)"
              strokeWidth="2"
              className="drop-shadow-sm"
            />
            {/* Bookings line */}
            <polyline
              points={points.map((p, i) => `${padding + i * xStep},${getY(p.bookings)}`).join(' ')}
              fill="none"
              stroke="rgb(168 85 247)"
              strokeWidth="2"
              className="drop-shadow-sm"
            />
            {/* Data points */}
            {points.map((p, i) => (
              <g key={i}>
                <circle
                  cx={padding + i * xStep}
                  cy={getY(p.purchases)}
                  r="4"
                  fill="rgb(59 130 246)"
                  className="hover:r-6 transition-all cursor-pointer"
                >
                  <title>{`${p.month}: ${p.purchases} ${labels?.purchases || 'Käufe'}`}</title>
                </circle>
                <circle
                  cx={padding + i * xStep}
                  cy={getY(p.bookings)}
                  r="4"
                  fill="rgb(168 85 247)"
                  className="hover:r-6 transition-all cursor-pointer"
                >
                  <title>{`${p.month}: ${p.bookings} ${labels?.bookings || 'Buchungen'}`}</title>
                </circle>
              </g>
            ))}
          </>
        )}

        {/* X-axis labels */}
        {points.map((p, i) => {
          let label = '';

          if (clusterSize === 12) {
            // Yearly clustering - show year
            label = p.month; // Already in YYYY format
          } else if (clusterSize === 6) {
            // Half-year clustering - show year and half
            const [year, month] = p.month.split('-');
            label = `${month <= '06' ? 'H1' : 'H2'} '${year.slice(-2)}`;
          } else if (clusterSize === 3) {
            // Quarterly clustering - show quarter
            const date = new Date(p.month + '-01');
            const quarter = Math.floor(date.getMonth() / 3) + 1;
            label = `Q${quarter} '${date.getFullYear().toString().slice(-2)}`;
          } else if (clusterSize === 2) {
            // Bi-monthly clustering - show month + year
            const date = new Date(p.month + '-01');
            label = date.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
          } else {
            // Monthly - show month + year
            const date = new Date(p.month + '-01');
            label = date.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
          }

          return (
            <text
              key={i}
              x={padding + i * xStep}
              y={height - padding + 20}
              textAnchor="middle"
              className="text-xs fill-gray-500"
            >
              {label}
            </text>
          );
        })}
      </svg>

      {/* Legend - only shown if labels are not provided */}
      {!labels && (
        <div className="flex justify-center gap-6 mt-4">
          {type === 'revenue' ? (
            <>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-success-500 rounded-full"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Gesamtumsatz</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-info-500 rounded-full"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Bezahlter Umsatz</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-info-500 rounded-full"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Käufe</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                <span className="text-sm text-gray-600 dark:text-gray-400">Buchungen</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LineChart;